import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Code} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand, toBrandCode} from '../../../contexts/brand-context'

/* Dev/preview only — internal tree names shouldn't leak to shoppers. */
const isPreviewOrDev = () => {
    if (process.env.NODE_ENV !== 'production') return true
    if (typeof window !== 'undefined' && window.location) {
        const params = new URLSearchParams(window.location.search)
        if (params.get('epieditmode') === 'true' || params.get('preview_token')) return true
    }
    return false
}

/* Temporary banner shown when Optimizely returns no content for the active
 * (brand, locale) tree. Replaces the silent 404 redirect so authors/devs can
 * see exactly which tree is missing content during the per-locale rollout.
 * Remove once all 6 trees are populated. */
const ContentNotFoundBanner = ({contentKey}) => {
    const {brandId} = useBrand()
    const {locale} = useMultiSite()
    if (!isPreviewOrDev()) return null
    const brandSlug = `${toBrandCode(brandId).toLowerCase()}-web`
    const localeId = locale?.id || locale?.alias || 'en-CA'

    return (
        <Box
            p={4}
            m={4}
            borderWidth="2px"
            borderColor="teal.500"
            borderStyle="dashed"
            bg="teal.50"
            borderRadius="md"
            data-testid="content-not-found-banner"
        >
            <Text color="teal.900" fontWeight="bold" mb={1}>
                ⚠️ No content found for this page
            </Text>
            <Text color="teal.800" fontSize="sm">
                Brand{' '}
                <Code colorScheme="teal" bg="teal.100" color="teal.900">
                    {brandSlug}
                </Code>{' '}
                + locale{' '}
                <Code colorScheme="teal" bg="teal.100" color="teal.900">
                    {localeId}
                </Code>
                {contentKey ? (
                    <>
                        {' '}does not have content authored under{' '}
                        <Code colorScheme="teal" bg="teal.100" color="teal.900">
                            {contentKey}
                        </Code>{' '}
                        yet.
                    </>
                ) : null}
            </Text>
        </Box>
    )
}

ContentNotFoundBanner.propTypes = {
    contentKey: PropTypes.string
}

export default ContentNotFoundBanner
